/**
 * TradingView-style range picker: 1D / 5D / 1M / 3M / 6M / YTD / 1Y / 5Y / All.
 *
 * Converts a time-range preset into a bar count for the current timeframe --
 * so "1M on 1h" asks for ~140 bars (21 trading days × 7 hours), "1M on 1d"
 * asks for 21 bars, and "YTD on 1d" asks for however many sessions have
 * printed since January 1st. Replaces the old "N bars" dropdown which
 * forced the trader to think in bar-counts rather than calendar durations.
 */
import { useMemo } from "react";

type RangeKey = "1D" | "5D" | "1M" | "3M" | "6M" | "YTD" | "1Y" | "5Y" | "All";

const RANGES: RangeKey[] = ["1D", "5D", "1M", "3M", "6M", "YTD", "1Y", "5Y", "All"];

// Trading-session bar counts per calendar unit, per timeframe. US cash
// equities assume 6.5 trading hours per session and ~252 sessions per year.
const BARS_PER_SESSION: Record<string, number> = {
  "1s": 23400, "5s": 4680, "10s": 2340, "15s": 1560, "30s": 780,
  "1m": 390, "2m": 195, "3m": 130, "5m": 78, "10m": 39, "15m": 26,
  "30m": 13, "45m": 9, "1h": 7, "2h": 4, "3h": 3, "4h": 2,
  "1d": 1,
  "1w": 1 / 5,
  "1M": 1 / 21,
};

function sessionsSinceJanFirst(): number {
  const now = new Date();
  const jan1 = new Date(now.getFullYear(), 0, 1);
  const calendarDays = Math.floor((now.getTime() - jan1.getTime()) / 86400_000);
  // ~5 trading days per 7 calendar days; close enough for a UI range picker.
  return Math.max(1, Math.round(calendarDays * (5 / 7)));
}

function sessionsFor(range: RangeKey): number {
  switch (range) {
    case "1D":  return 1;
    case "5D":  return 5;
    case "1M":  return 21;
    case "3M":  return 63;
    case "6M":  return 126;
    case "YTD": return sessionsSinceJanFirst();
    case "1Y":  return 252;
    case "5Y":  return 1260;
    case "All": return 50_000; // engine cap
  }
}

function barsFor(range: RangeKey, timeframe: string): number {
  const perSession = BARS_PER_SESSION[timeframe] ?? 1;
  const sessions = sessionsFor(range);
  const bars = Math.round(sessions * perSession);
  // Clamp into the gateway's accepted window.
  return Math.min(50_000, Math.max(100, bars));
}

export function RangePicker({
  timeframe, value, onChange,
}: {
  timeframe: string;
  value: number;
  onChange: (bars: number) => void;
}) {
  // Highlight the preset whose computed bar count matches the current value
  // most closely (within 15%) -- lets us keep a persistent `value` prop
  // without maintaining a parallel "which preset" state.
  const active = useMemo(() => {
    for (const r of RANGES) {
      const b = barsFor(r, timeframe);
      if (Math.abs(b - value) / Math.max(b, value) < 0.15) return r;
    }
    return null;
  }, [timeframe, value]);

  return (
    <div className="range-picker" role="group" aria-label="Chart range">
      {RANGES.map((r) => (
        <button
          key={r}
          type="button"
          className={`range-btn ${active === r ? "active" : ""}`}
          title={`Load ${sessionsFor(r)} trading ${sessionsFor(r) === 1 ? "session" : "sessions"} at ${timeframe}`}
          onClick={() => onChange(barsFor(r, timeframe))}
        >{r}</button>
      ))}
    </div>
  );
}
